import { Dialog as DialogPrimitive } from "@base-ui/react/dialog"
import { Maximize2Icon, XIcon } from "lucide-react"
import { useRef, type FocusEvent, type ReactNode } from "react"
import { Button } from "@/primitives/button.tsx"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { isEditable } from "@/shortcuts/dispatch.ts"
import { ShortcutScope } from "@/shortcuts/use-shortcut.ts"
import { ButtonLink } from "./button-link.tsx"
import { Key } from "./key.tsx"
import { handsBack, type FocusFrom } from "./page-scroll.ts"

/*
 * The Ticket peek: a ticket or an Epic opened over the view it was clicked in, holding its whole
 * page, so a quick look at a ticket never loses the Member's place. It is a large dialog, most of
 * the viewport (`size-peek`), with a 48px bar holding the ticket's key, the page's actions, "Open
 * full page" and ×, stuck to the top of its one scroll container. That bar is the height of a
 * PageHeader, so a ticket's rail, which sticks under a page's header, sticks under it here too.
 *
 * Every shortcut bound inside it belongs to it (`ShortcutScope`): the ticket page's are live here,
 * and the view beneath stands aside, as it does under any overlay. Esc pressed in a text field is
 * the field's, so it cancels an edit first; anywhere else it closes the peek. Clicking outside, ×
 * and Esc all close it through `onClose`, which decides whether it goes: the open peek is the
 * caller's, as the URL holds it.
 */
export function TicketPeek({
  open,
  ticketKey,
  fullPage,
  actions,
  onClose,
  onClosed,
  children,
}: {
  open: boolean
  /** The open ticket's or Epic's key, which names the peek. */
  ticketKey: string
  /** Where its full page is. */
  fullPage: string
  /** The page's own actions on it, such as Copy link and its ⋯ menu. */
  actions?: ReactNode
  onClose: () => void
  /** It has finished closing, its exit played out. */
  onClosed?: () => void
  children: ReactNode
}) {
  const popup = useRef<HTMLDivElement>(null)
  const scroller = useRef<HTMLDivElement>(null)
  // Whether the scroller itself held focus, as it does when the peek opens, rather than a control.
  const heldItself = useRef(false)

  // As the page scroll does, the scroller takes focus back from an overlay opened over it (a
  // picker opened by its shortcut) as that closes, rather than letting it land on the first control
  // in the peek. Focus moving within the peek, which is itself an overlay, comes from elsewhere.
  const onFocus = (event: FocusEvent<HTMLDivElement>) => {
    const element = event.currentTarget
    // A picker in the peek is its child in React but portalled out of it, so focus there isn't its.
    if (!element.contains(event.target)) return
    const previous = event.relatedTarget
    const from: FocusFrom =
      previous === null ||
      (previous instanceof Element &&
        !popup.current?.contains(previous) &&
        previous.closest("[data-base-ui-portal]") !== null)
        ? "overlay"
        : "elsewhere"
    if (event.target !== element && handsBack(heldItself.current, from)) {
      element.focus({ preventScroll: true })
      return
    }
    heldItself.current = event.target === element
  }

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChangeComplete={(opened) => !opened && onClosed?.()}
      onOpenChange={(next, details) => {
        if (next) return
        const target = details.event?.target
        // Esc in a field is the field's: the peek stays, and the press goes on to the field's own
        // Esc, which Base UI would otherwise stop.
        if (
          details.reason === "escape-key" &&
          target instanceof HTMLElement &&
          isEditable(target)
        ) {
          details.cancel()
          details.allowPropagation()
          return
        }
        onClose()
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop className="fixed inset-0 isolate z-50 bg-scrim duration-150 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:duration-100 data-closed:fade-out-0" />
        <DialogPrimitive.Popup
          ref={popup}
          initialFocus={scroller}
          data-slot="ticket-peek"
          className="fixed top-1/2 left-1/2 z-50 flex size-peek -translate-1/2 flex-col overflow-hidden rounded-xl bg-background text-ui text-foreground shadow-dialog duration-150 ease-snappy outline-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-98 data-closed:animate-out data-closed:duration-100 data-closed:fade-out-0 data-closed:zoom-out-98"
        >
          <ShortcutScope value={popup}>
            <div
              ref={scroller}
              tabIndex={-1}
              onFocus={onFocus}
              className="flex min-h-0 flex-1 flex-col overflow-y-auto outline-none"
            >
              <div className="sticky top-0 z-30 flex h-12 shrink-0 items-center gap-2 border-b border-border-subtle bg-background px-4">
                <DialogPrimitive.Title className="flex min-w-0 items-center text-ui font-medium">
                  <Key value={ticketKey} />
                </DialogPrimitive.Title>
                <div className="ml-auto flex items-center gap-1">
                  {actions}
                  <ButtonLink to={fullPage} variant="ghost" size="sm">
                    <Maximize2Icon data-icon="inline-start" />
                    Open full page
                  </ButtonLink>
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <DialogPrimitive.Close
                          render={<Button variant="ghost" size="icon-sm" aria-label="Close" />}
                        />
                      }
                    >
                      <XIcon />
                    </TooltipTrigger>
                    <TooltipContent>Close</TooltipContent>
                  </Tooltip>
                </div>
              </div>
              {children}
            </div>
          </ShortcutScope>
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
